import DOMPurify from "dompurify";
import { marked } from "marked";
import { truncateText } from "./format.ts";

const allowedTags = [
  "a",
  "b",
  "blockquote",
  "br",
  "button",
  "code",
  "del",
  "details",
  "div",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "hr",
  "i",
  "li",
  "ol",
  "p",
  "pre",
  "span",
  "strong",
  "summary",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "ul",
  "img",
];

const allowedAttrs = [
  "class",
  "href",
  "rel",
  "target",
  "title",
  "start",
  "src",
  "alt",
  "data-code",
  "type",
  "aria-label",
];
const sanitizeOptions = {
  ALLOWED_TAGS: allowedTags,
  ALLOWED_ATTR: allowedAttrs,
  ADD_DATA_URI_TAGS: ["img"],
};

let hooksInstalled = false;
const MARKDOWN_CHAR_LIMIT = 140_000;
const MARKDOWN_PARSE_LIMIT = 40_000;
const MARKDOWN_CACHE_LIMIT = 200;
const MARKDOWN_CACHE_MAX_CHARS = 50_000;
const INLINE_DATA_IMAGE_RE = /^data:image\/[a-z0-9.+-]+;base64,/i;
const markdownCache = new Map<string, string>();
const TAIL_LINK_BLUR_CLASS = "chat-link-tail-blur";
const FW_STATUS_FENCE_RE = /^fw:status[ \t]*\n([\s\S]*?)^\/fw:status[ \t]*$/gim;
const ACTION_HEADING_CLASS = "chat-action-heading";
const ACTION_HEADING_LABELS = new Map([
  ["NEXT STEPS", { level: 1, label: "NEXT STEPS", variant: "next-steps" }],
  ["DO NOW", { level: 2, label: "DO NOW", variant: "do-now" }],
  ["WAITING ON", { level: 2, label: "WAITING ON", variant: "waiting-on" }],
  ["LATER", { level: 2, label: "LATER", variant: "later" }],
  ["CONTEXT", { level: 2, label: "Context", variant: "context" }],
  ["CONTEXT / DETAILS", { level: 2, label: "Context / details", variant: "context" }],
  ["DETAILS", { level: 2, label: "Details", variant: "context" }],
]);

function getCachedMarkdown(key: string): string | null {
  const cached = markdownCache.get(key);
  if (cached === undefined) {
    return null;
  }
  markdownCache.delete(key);
  markdownCache.set(key, cached);
  return cached;
}

function setCachedMarkdown(key: string, value: string) {
  markdownCache.set(key, value);
  if (markdownCache.size <= MARKDOWN_CACHE_LIMIT) {
    return;
  }
  const oldest = markdownCache.keys().next().value;
  if (oldest) {
    markdownCache.delete(oldest);
  }
}

function installHooks() {
  if (hooksInstalled) {
    return;
  }
  hooksInstalled = true;

  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (!(node instanceof HTMLAnchorElement)) {
      return;
    }
    const href = node.getAttribute("href");
    if (!href) {
      return;
    }
    node.setAttribute("rel", "noreferrer noopener");
    node.setAttribute("target", "_blank");
    if (href.toLowerCase().includes("tail")) {
      node.classList.add(TAIL_LINK_BLUR_CLASS);
    }
  });
}

export function toSanitizedMarkdownHtml(markdown: string): string {
  const input = markdown.trim();
  if (!input) {
    return "";
  }
  installHooks();
  if (input.length <= MARKDOWN_CACHE_MAX_CHARS) {
    const cached = getCachedMarkdown(input);
    if (cached !== null) {
      return cached;
    }
  }
  const truncated = truncateText(input, MARKDOWN_CHAR_LIMIT);
  const suffix = truncated.truncated
    ? `\n\n… truncated (${truncated.total} chars, showing first ${truncated.text.length}).`
    : "";
  if (truncated.text.length > MARKDOWN_PARSE_LIMIT) {
    // Large plain-text replies should stay readable without inheriting the
    // capped code-block chrome, while still preserving whitespace for logs
    // and other structured text that commonly trips the parse guard.
    const html = renderEscapedPlainTextHtml(`${truncated.text}${suffix}`);
    const sanitized = DOMPurify.sanitize(html, sanitizeOptions);
    if (input.length <= MARKDOWN_CACHE_MAX_CHARS) {
      setCachedMarkdown(input, sanitized);
    }
    return sanitized;
  }
  const displayMarkdown = normalizeActionHeadings(`${truncated.text}${suffix}`);
  let rendered: string;
  try {
    rendered = renderMarkdownWithStatusFences(displayMarkdown);
  } catch (err) {
    // Fall back to escaped plain text when marked.parse() throws (e.g.
    // infinite recursion on pathological markdown patterns — #36213).
    console.warn("[markdown] marked.parse failed, falling back to plain text:", err);
    const escaped = escapeHtml(displayMarkdown);
    rendered = `<pre class="code-block">${escaped}</pre>`;
  }
  const sanitized = DOMPurify.sanitize(rendered, sanitizeOptions);
  if (input.length <= MARKDOWN_CACHE_MAX_CHARS) {
    setCachedMarkdown(input, sanitized);
  }
  return sanitized;
}

function normalizeActionHeadings(markdown: string): string {
  return markdown
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => {
      const normalized = normalizeHeadingLabel(line);
      const actionHeading = normalized ? ACTION_HEADING_LABELS.get(normalized) : undefined;
      if (!actionHeading) {
        return line;
      }
      return `${"#".repeat(actionHeading.level)} ${actionHeading.label}`;
    })
    .join("\n");
}

function normalizeHeadingLabel(line: string): string | null {
  let value = line.trim();
  if (!value) {
    return null;
  }
  value = value.replace(/^#{1,6}\s+/, "").trim();
  value = value.replace(/^\*\*(.*?)\*\*$/, "$1").trim();
  value = value.replace(/^__(.*?)__$/, "$1").trim();
  value = value.replace(/:$/, "").trim();
  const compact = value.replace(/\s+/g, " ").toUpperCase();
  return ACTION_HEADING_LABELS.has(compact) ? compact : null;
}

function renderMarkdownWithStatusFences(markdown: string): string {
  FW_STATUS_FENCE_RE.lastIndex = 0;
  const renderedParts: string[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = FW_STATUS_FENCE_RE.exec(markdown)) !== null) {
    const before = markdown.slice(cursor, match.index);
    if (before.trim()) {
      renderedParts.push(renderMarkdownFragment(before));
    }
    renderedParts.push(renderStatusFenceCard(match[1] ?? ""));
    cursor = match.index + match[0].length;
  }

  const after = markdown.slice(cursor);
  if (after.trim()) {
    renderedParts.push(renderMarkdownFragment(after));
  }

  if (renderedParts.length === 0) {
    return renderMarkdownFragment(markdown);
  }

  return renderedParts.join("\n");
}

function renderMarkdownFragment(markdown: string): string {
  return marked.parse(markdown, {
    renderer: htmlEscapeRenderer,
    gfm: true,
    breaks: true,
  }) as string;
}

type StatusFenceField = "now" | "tonight" | "tomorrow";

type StatusFence = {
  campaign: string;
  state: string;
  waitingOn: string;
  now: string[];
  tonight: string[];
  tomorrow: string[];
  lastArtifact: string;
  updated: string;
};

function renderStatusFenceCard(body: string): string {
  const status = parseStatusFence(body);
  const title = status.campaign || "Thread status";
  const state = status.state || "status";
  const waitingOn = status.waitingOn || "none";
  return [
    '<details class="chat-status-fence-card">',
    '<summary class="chat-status-fence-card__summary">',
    '<span class="chat-status-fence-card__eyebrow">machine status</span>',
    `<strong class="chat-status-fence-card__title">${escapeHtml(title)}</strong>`,
    `<span class="chat-status-fence-card__meta">${escapeHtml(state)} · waiting on ${escapeHtml(waitingOn)}</span>`,
    "</summary>",
    '<div class="chat-status-fence-card__body">',
    renderStatusFenceList("Do now", status.now),
    renderStatusFenceList("Tonight", status.tonight),
    renderStatusFenceList("Tomorrow", status.tomorrow),
    renderStatusFenceFact("Last artifact", status.lastArtifact),
    renderStatusFenceFact("Updated", status.updated),
    "</div>",
    "</details>",
  ]
    .filter(Boolean)
    .join("");
}

function parseStatusFence(body: string): StatusFence {
  const status: StatusFence = {
    campaign: "",
    state: "",
    waitingOn: "",
    now: [],
    tonight: [],
    tomorrow: [],
    lastArtifact: "",
    updated: "",
  };
  let activeList: StatusFenceField | null = null;

  for (const rawLine of body.replace(/\r\n?/g, "\n").split("\n")) {
    const line = rawLine.trim();
    if (!line) {
      continue;
    }
    const listHeader = /^(now|tonight|tomorrow):\s*$/i.exec(line);
    if (listHeader) {
      activeList = listHeader[1].toLowerCase() as StatusFenceField;
      continue;
    }
    const listItem = /^[-*]\s+(.*)$/.exec(line);
    if (listItem && activeList) {
      status[activeList].push(listItem[1].trim());
      continue;
    }
    activeList = null;
    const field = /^(campaign|state|waitingOn|lastArtifact|updated):\s*(.*)$/i.exec(line);
    if (!field) {
      continue;
    }
    const key = field[1].toLowerCase();
    const value = field[2].trim();
    if (key === "waitingon") {
      status.waitingOn = value;
    } else if (key === "lastartifact") {
      status.lastArtifact = value;
    } else if (key === "campaign" || key === "state" || key === "updated") {
      status[key] = value;
    }
  }

  return status;
}

function renderStatusFenceList(label: string, items: string[]): string {
  if (!items.length) {
    return "";
  }
  const renderedItems = items.map((item) => `<li>${escapeHtml(item)}</li>`).join("");
  return `<div class="chat-status-fence-card__section"><span>${escapeHtml(label)}</span><ul>${renderedItems}</ul></div>`;
}

function renderStatusFenceFact(label: string, value: string): string {
  if (!value) {
    return "";
  }
  return `<p class="chat-status-fence-card__fact"><span>${escapeHtml(label)}:</span> ${escapeHtml(value)}</p>`;
}

function plainTextFromInlineHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
}

// Prevent raw HTML in chat messages from being rendered as formatted HTML.
// Display it as escaped text so users see the literal markup.
// Security is handled by DOMPurify, but rendering pasted HTML (e.g. error
// pages) as formatted output is confusing UX (#13937).
const htmlEscapeRenderer = new marked.Renderer();
htmlEscapeRenderer.heading = function ({
  tokens,
  depth,
}: {
  tokens: unknown[];
  depth: number;
}) {
  const inlineHtml = this.parser.parseInline(tokens as never[]);
  const normalized = normalizeHeadingLabel(plainTextFromInlineHtml(inlineHtml));
  const actionHeading = normalized ? ACTION_HEADING_LABELS.get(normalized) : undefined;
  if (!actionHeading) {
    return `<h${depth}>${inlineHtml}</h${depth}>\n`;
  }
  const tagDepth = Math.min(Math.max(actionHeading.level, 1), 6);
  const classes = `${ACTION_HEADING_CLASS} ${ACTION_HEADING_CLASS}--${actionHeading.variant}`;
  return `<h${tagDepth} class="${classes}">${escapeHtml(actionHeading.label)}</h${tagDepth}>\n`;
};
htmlEscapeRenderer.html = ({ text }: { text: string }) => escapeHtml(text);
htmlEscapeRenderer.image = (token: { href?: string | null; text?: string | null }) => {
  const label = normalizeMarkdownImageLabel(token.text);
  const href = token.href?.trim() ?? "";
  if (!INLINE_DATA_IMAGE_RE.test(href)) {
    return escapeHtml(label);
  }
  return `<img class="markdown-inline-image" src="${escapeHtml(href)}" alt="${escapeHtml(label)}">`;
};

function normalizeMarkdownImageLabel(text?: string | null): string {
  const trimmed = text?.trim();
  return trimmed ? trimmed : "image";
}

htmlEscapeRenderer.code = ({
  text,
  lang,
  escaped,
}: {
  text: string;
  lang?: string;
  escaped?: boolean;
}) => {
  const langClass = lang ? ` class="language-${escapeHtml(lang)}"` : "";
  const safeText = escaped ? text : escapeHtml(text);
  const codeBlock = `<pre><code${langClass}>${safeText}</code></pre>`;
  const langLabel = lang ? `<span class="code-block-lang">${escapeHtml(lang)}</span>` : "";
  const attrSafe = text
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const copyBtn = `<button type="button" class="code-block-copy" data-code="${attrSafe}" aria-label="Copy code"><span class="code-block-copy__idle">Copy</span><span class="code-block-copy__done">Copied!</span></button>`;
  const header = `<div class="code-block-header">${langLabel}${copyBtn}</div>`;

  const trimmed = text.trim();
  const isJson =
    lang === "json" ||
    (!lang &&
      ((trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))));

  if (isJson) {
    const lineCount = text.split("\n").length;
    const label = lineCount > 1 ? `JSON &middot; ${lineCount} lines` : "JSON";
    return `<details class="json-collapse"><summary>${label}</summary><div class="code-block-wrapper">${header}${codeBlock}</div></details>`;
  }

  return `<div class="code-block-wrapper">${header}${codeBlock}</div>`;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function renderEscapedPlainTextHtml(value: string): string {
  return `<div class="markdown-plain-text-fallback">${escapeHtml(value.replace(/\r\n?/g, "\n"))}</div>`;
}
